import React from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import Skeleton from "components/ui/Skeleton";
import { fmtDate, fmtCompact } from "../utils";

const SERIES = [
  { key: "sent", label: "Sent", color: "var(--color-primary)" },
  { key: "delivered", label: "Delivered", color: "var(--color-secondary)" },
  { key: "opened", label: "Opened", color: "var(--color-success)" },
  { key: "clicked", label: "Clicked", color: "var(--color-info)" },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-surface px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-text_primary mb-1.5">
        {fmtDate(label, { day: "2-digit", month: "short", year: "numeric" })}
      </p>
      <div className="space-y-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2 text-xs">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
            <span className="text-text_secondary">{p.name}:</span>
            <span className="font-semibold text-text_primary tabular-nums">{fmtCompact(p.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const TrendChart = ({ data, loading, rangeDays }) => {
  return (
    <div className="rounded-2xl border border-border bg-surface p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="font-semibold text-text_primary">Sending Activity</h2>
          <p className="text-xs text-text_muted mt-0.5">Last {rangeDays} days</p>
        </div>
        <div className="flex items-center gap-3 text-xs text-text_secondary flex-wrap">
          {SERIES.map((s) => (
            <span key={s.key} className="flex items-center gap-1.5">
              <span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: s.color }} />
              {s.label}
            </span>
          ))}
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" rounded="rounded-xl" />
      ) : (
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={data} margin={{ top: 5, right: 8, left: -16, bottom: 0 }}>
            <defs>
              {SERIES.map((s) => (
                <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={s.color} stopOpacity={0.35} />
                  <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={(d) => fmtDate(d)}
              tick={{ fill: "var(--color-text-muted)", fontSize: 11 }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              minTickGap={24}
            />
            <YAxis tickFormatter={fmtCompact} tick={{ fill: "var(--color-text-muted)", fontSize: 11 }} axisLine={false} tickLine={false} width={40} />
            <Tooltip content={<CustomTooltip />} />
            {SERIES.map((s) => (
              <Area key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} fill={`url(#grad-${s.key})`} />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default TrendChart;
